/**
 * Vercel AI SDK adapter for browser automation tool
 */

import { tool as createTool } from 'ai';
import { z } from 'zod';
import { executeBrowserTask } from './core.js';
import type { BrowserConfig } from './types.js';
import { BROWSER_TOOL_DESCRIPTION } from './prompts.js';

/**
 * Create Vercel AI SDK tool for browser automation
 * 
 * @param config - Optional browser worker configuration
 * @returns Vercel AI SDK tool
 * 
 * @example
 * ```typescript
 * import { generateText } from 'ai';
 * import { anthropic } from '@ai-sdk/anthropic';
 * import { createBrowserTool } from 'morphsdk/tools/browser/vercel';
 * 
 * const browserTool = createBrowserTool({
 *   apiUrl: 'https://browser-worker.example.com'
 * });
 * 
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   tools: { browserTask: browserTool },
 *   prompt: 'Test the checkout flow at https://3000-abc.e2b.dev',
 *   maxSteps: 5
 * });
 * ```
 */
export function createBrowserTool(config?: BrowserConfig) {
  const schema = z.object({
    task: z.string().describe('Natural language description of what to do (e.g., "Test checkout flow for buying a pineapple")'),
    url: z.string().optional().describe('Starting URL (e.g., https://3000-xyz.e2b.dev)'),
    maxSteps: z.number().min(1).max(50).default(10).describe('Maximum number of browser actions to take'),
    region: z.enum(['sfo', 'lon']).default('sfo').describe('Browserless region: sfo (US West) or lon (Europe)'),
  });

  return createTool({
    description: BROWSER_TOOL_DESCRIPTION,
    inputSchema: schema,
    execute: async (params) => {
      const { task, url, maxSteps, region } = params;
      const result = await executeBrowserTask(
        { task, url, maxSteps, region },
        config
      );

      // Return minimal summary for agent context (to save tokens)
      // Full result with urls, errors, action_history, judgement, etc. is available
      // when calling executeBrowserTask() directly outside of agent tools
      if (result.success) {
        return {
          success: true,
          result: result.result,
          stepsTaken: result.stepsTaken,
          executionTimeMs: result.executionTimeMs,
        };
      }

      return {
        success: false,
        error: result.error,
      };
    },
  });
}

/**
 * Default browser tool for Vercel AI SDK
 */
export const browserTool = createBrowserTool();
